// --- Enums ---

export enum NiiRunType {
  FEATURE_IMPACT = 'feature_impact',
  TR_IMPACT = 'tr_impact',
  GPL_AUDIT = 'gpl_audit'
}

export enum NiiRunStatus {
  QUEUED = 'queued',
  RUNNING = 'running',
  COMPLETE = 'complete',
  ERROR = 'error',
  CANCELLED = 'cancelled'
}

// --- Run interfaces ---

/** Single run item from GET /api/nii/runs or GET /api/nii/runs/:runId */
export interface RunStatusResponse {
  runId: string;
  type: NiiRunType;
  status: NiiRunStatus;
  name: string | null;
  progress: number | null;
  stage: string | null;
  reportCount: number | null;
  releaseName: string | null;
  gplVersion: string | null;
  createdBy: string | null;
  createdAt: string;
  completedAt: string | null;
  error: { stage: string; detail: string } | null;
}

/** Paginated list of runs — GET /api/nii/runs */
export interface RunListResponse {
  data: RunStatusResponse[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// --- Summary cards (landing page) ---

/** One card per release anchor — from GET /api/nii/runs?groupBy=release */
export interface SummaryCard {
  anchor: string;
  totalRuns: number;
  totalReports: number;
  completedCount: number;
  errorCount: number;
  latestAt: string | null;
}

/** Full response from GET /api/nii/runs?type=X&groupBy=release */
export interface SummaryCardResponse {
  cards: SummaryCard[];
  availableAnchors: string[];
}

// --- Report interfaces ---

/** Single report summary — from GET /api/nii/reports?... */
export interface ReportSummary {
  reportId: string;
  runId: string;
  runName: string | null;
  type: NiiRunType;
  status: NiiRunStatus;
  nodeName: string | null;
  releaseName: string | null;
  gplVersion: string | null;
  auditResult: string | null;
  totalDiscrepancies: number | null;
  complianceRate: number | null;
  createdBy: string | null;
  createdAt: string;
  completedAt: string | null;
  dataSourceKind: 'enm_live' | 'upload' | null;
  enmEndpointId: string | null;
  enmEndpointName: string | null;
  uploadId: string | null;
  uploadFilename: string | null;
}

/** Paginated list of reports — GET /api/nii/reports?... */
export interface ReportListResponse {
  data: ReportSummary[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** Report status poll — GET /api/nii/reports/:reportId/status */
export interface ReportStatusPollResponse {
  reportId: string;
  runId: string;
  type: NiiRunType;
  nodeName: string | null;
  status: NiiRunStatus;
  progress: number | null;
  stage: string | null;
  error: { stage: string; detail: string } | null;
}

// --- Generation (create runs) ---

/** Data source for a run */
export type RunDataSource =
  | { kind: 'enm_live'; enmEndpointIds: string[] }
  | { kind: 'upload'; uploadId: string }
  | { kind: 'epp'; enmEndpointIds: string[] };

/** Single run specification in the create request */
export interface RunSpec {
  type: NiiRunType;
  name: string;
  release?: { name: string; identity?: string };
  gplVersion?: string;
  dataSource: RunDataSource;
}

/** POST /api/nii/runs request body */
export interface CreateRunsRequest {
  runs: RunSpec[];
}

/** POST /api/nii/runs response (202 Accepted) */
export interface CreateRunsResponse {
  runs: {
    runId: string;
    type: NiiRunType;
    status: NiiRunStatus;
    releaseName: string | null;
    gplVersion: string | null;
    name: string | null;
  }[];
}

// --- ENM ---

// ENM endpoint from GET /api/enm — unchanged
export interface EnmEndpoint {
  id: string;
  name: string;
  category: string;
  url: string;
  status: string;
}

// --- CPI Releases (generation form dropdown) ---

/** Single CPI release — from GET /api/nii/releases */
export interface CpiRelease {
  release_id: string;
  library_identity: string;
  date: string;
}

/** GET /api/nii/releases response — flat array */
export type CpiReleasesResponse = CpiRelease[];

// --- Upload ---

/** POST /api/nii/uploads?purpose=network_inventory response */
export interface UploadResponse {
  uploadId: string;
  purpose: string;
  filename: string;
  sizeBytes: number;
  nodeNames: string[] | null;
  gplVersion: string | null;
  contents: unknown | null;
  createdAt: string;
  validation: {
    valid: boolean;
    featureCount: number;
    hardwareCount: number;
  } | null;
}

// --- WebSocket event types ---

/** WebSocket NII_RUN status-change event payload */
export interface NiiRunWsEvent {
  runId: string;
  type: NiiRunType;
  status: NiiRunStatus;
  releaseName: string | null;
  gplVersion: string | null;
  completedAt: string | null;
}

/** WebSocket NII_REPORT status-change event payload */
export interface NiiReportWsEvent {
  runId: string;
  status: NiiRunStatus;
}

// --- GPL Script Management ---

/** GPL script bundle — from GET/POST /api/nii/uploads?purpose=gpl_scripts */
export interface GplScriptBundle {
  uploadId: string;
  purpose: string;
  filename: string;
  sizeBytes: number;
  createdAt: string;
  gplVersion: string;
  contents: string[];
  nodeNames: string[] | null;
  validation: unknown | null;
}

// --- GPL Audit Report Content ---

/** Single GPL audit finding — one entry in data[] from GET /api/nii/reports/:reportId */
export interface GplFinding {
  category: string;
  status: string;
  gpl_source: string;
  mo_class_name: string;
  parameter_name: string;
  gpl_value: string;
  matched_dn: string;
  node_value: string;
  details: string | null;       // JSON string or null — parse for display
}

/** GPL audit report metadata — documentMetadata in response */
export interface GplReportDocumentMetadata {
  title: string;
  gpl_version: string;
  date: string;
  audit_method: string;
  summary: {
    node_name: string;
    total_discrepancies: number;
    compliance_rate: number;
    by_category: Record<string, number>;
    by_status: Record<string, number>;
  };
}

/** Paginated GPL audit report content — GET /api/nii/reports/:reportId */
export interface GplReportContentResponse {
  documentMetadata: GplReportDocumentMetadata;
  data: GplFinding[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  filtersData: Record<string, string[]>;
}

/** Tree node for GPL findings tree view — grouped by MO class (client-side) */
export interface GplFindingTreeNode {
  moClassName: string;
  children: GplFindingTreeGroup[];
}

/** Sub-group within an MO class in tree view */
export interface GplFindingTreeGroup {
  label: string;
  count: number;
  findings: GplFinding[];
}

/** Single entry in the audit runs timeline */
export interface GplAuditRunHistoryEntry {
  runId: string;
  reportId: string;
  auditName: string;
  auditResult: string;
  discrepancyCount: number;
  duration: string | null;
  createdAt: string;
}
